import React, { useEffect, useState } from 'react';
import '../styles/inspectionClosure.css';

import {
  UserOutlined,
  CheckCircleOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { Typography, Input, Button, Space, Timeline, Row, Col } from 'antd';
import { intervalToDuration, format, parseISO } from 'date-fns';
import { isMobile } from 'react-device-detect';

import { usePatchInspection } from '../api/patchInspectionCommand';
import { useInspectionTicketView } from '../hooks/useInspectionTicketView';
import { useInspectionStore } from '../store/inspection.store';
import { LogStatus } from '../type';
import { InspectionIcon } from '@/components/icons/InspectionIcon';

const { Title, Text, Paragraph } = Typography;
const NOTE_MAX_LENGTH = 150;

const getIconInspection = (status: string) => {
  if ('STATUS_END_INSPECTION' === status) {
    return <CheckCircleOutlined />;
  }
  if ('STATUS_START_INSPECTION' === status) {
    return <InspectionIcon />;
  }
  if ('STATUS_PRODUCT_STATUS_LOCKED_UNLOCKED' === status) {
    return <LockOutlined />;
  }
};

//covert duration second to minutes and seconds
const convertDuration = (duration: string) => {
  const seconds = parseInt(duration, 10);
  const { minutes = 0, seconds: secs = 0 } = intervalToDuration({
    start: 0,
    end: seconds * 1000,
  });

  return `${minutes}min and ${secs}s`;
};

const InspectionTimelineItem = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <>
    <Title level={5}>{title}</Title>
    <Text
      style={{
        color: '#595959',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '125%',
      }}
    >
      {description}
    </Text>
  </>
);
export const InspectionClosure = ({
  updateStep,
  inspectionTicketId,
}: {
  inspectionTicketId: string;
  updateStep: () => void;
}) => {
  //Mutation
  const { mutateAsync: closeInspection } = usePatchInspection({});
  const { clearInspectionTicket } = useInspectionTicketView();

  //Locals states

  const [inspectionSummary, setInspectionSummary] = useState<any>(null);
  const [currentMaxLength, setCurrentMaxLength] = useState(0);
  const [inspectionLogs, setInspectionLogs] = useState<any>([]);

  const setNote = useInspectionStore((state) => state.setNote);

  const note = useInspectionStore((state) => state.note);

  useEffect(() => {
    let isMounted = true;
    const closureInspection = async () => {
      try {
        if (!inspectionTicketId) return;

        const resp = await closeInspection({
          ticketId: inspectionTicketId,
          close: true,
        });

        if (isMounted) {
          setInspectionSummary(resp.ticket);
        }
      } catch (error) {
        console.error('Error closing inspection:', error);
      }
    };

    closureInspection();

    return () => {
      isMounted = false;
    };
  }, [inspectionTicketId, closeInspection]);

  //create function to format date "2025-04-29T09:17:08.903751052Z"  to March 26, 2025 - 22:11
  const formatDate = (dateString: string) => {
    const date = parseISO(dateString);

    return format(date, 'MMMM d, yyyy - HH:mm');
  };

  useEffect(() => {
    const prepareLog = () => {
      const logs = inspectionSummary?.logs.reverse().map((log: any) => ({
        dot: getIconInspection(log.status),
        children: (
          <InspectionTimelineItem
            title={LogStatus[log.status]}
            description={formatDate(log.created_at)}
          />
        ),
      }));
      setInspectionLogs(logs || []);
    };
    if (inspectionSummary) {
      prepareLog();
    }
  }, [inspectionSummary]);

  const handleSaveAndExit = async () => {
    try {
      await closeInspection({
        ticketId: inspectionTicketId,
        note: note,
      });

      clearInspectionTicket(); // Reset the ticket in the store
      updateStep();
    } catch (error) {
      console.error('Error saving and exiting:', error);
    }
  };

  return (
    <>
      <Row style={{ padding: '1rem', marginLeft: '24px' }}>
        <Col xs={24}>
          <Paragraph
            style={{
              fontSize: '1rem',
              fontWeight: 400,
              color: '#595959',
              lineHeight: '135%',
            }}
          >
            Review the inspection actions and add any additional notes.
          </Paragraph>
        </Col>
      </Row>
      <Row
        style={{
          border: '1px solid #d9d9d9',
          borderBottom: '0px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          borderBottomLeftRadius: '0px',
          borderBottomRightRadius: '0px',
          padding: '1rem',
          marginLeft: '16px',
          marginRight: '16px',
        }}
      >
        <Col xs={24} sm={18}>
          <Space
            style={{
              paddingBottom: '8px',
            }}
          >
            <UserOutlined />
            <Title
              style={{ fontSize: '14px', fontWeight: 500, lineHeight: '125%' }}
            >
              Support agent in charge:
            </Title>
          </Space>
          <Text
            style={{
              width: '100%',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '135%',
              color: '#595959',
            }}
          >
            {inspectionSummary?.username}
          </Text>
        </Col>
        <Col xs={24} sm={6}>
          <Space
            direction="vertical"
            style={{
              background: '#FAFAFA',
              width: '100%',
              borderRadius: '4px',
              border: '1px solid #D9D9D9D9',
              padding: '8px',
            }}
          >
            <Text strong>Total inspection duration:</Text>
            <Text>{convertDuration(inspectionSummary?.duration)}</Text>
          </Space>
        </Col>
      </Row>
      <Row
        style={{
          border: '0.0625rem solid #d9d9d9',
          borderTopLeftRadius: '0rem',
          borderTopRightRadius: '0rem',
          borderBottomLeftRadius: '0.5rem',
          borderBottomRightRadius: '0.5rem',
          padding: '1rem',
          marginLeft: '16px',
          marginRight: '16px',
        }}
      >
        <Col xs={24}>
          <Space direction="vertical">
            <Title level={4} style={{ paddingBottom: '1rem' }}>
              Inspection logs
            </Title>
            <Timeline className="inspection-closure" items={inspectionLogs} />
          </Space>
        </Col>
      </Row>
      <Row style={{ marginLeft: '16px', marginRight: '16px' }}>
        <Col xs={24}>
          <Title level={4} style={{ paddingTop: '1rem' }}>
            Additional notes (optional)
          </Title>
        </Col>
        <Col xs={24}>
          <Row>
            <Col xs={24} sm={18}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Input.TextArea
                  rows={4}
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    setCurrentMaxLength(e.target.value.length);
                  }}
                  placeholder="Add any relevant notes here (please limit your response to 150 characters)."
                  maxLength={150}
                />
                <Text
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '22px',
                    justifyContent: 'flex-end',
                    display: 'inline-flex',
                    width: '100%',
                    color: '#00000040',
                    padding: '2px',
                  }}
                >{`${currentMaxLength} / ${NOTE_MAX_LENGTH}`}</Text>
              </Space>
            </Col>
            {!isMobile && (
              <Col push={2} sm={4}>
                <Button
                  type="primary"
                  style={{
                    marginTop: '16px',
                    borderRadius: '2px',
                    fontSize: '16px',
                    lineHeight: '24px',
                    fontWeight: 400,
                  }}
                  onClick={() => {
                    handleSaveAndExit();
                  }}
                >
                  Save and exit
                </Button>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </>
  );
};
